<template>
  <div class="querybuilder-toolbar box grow">
    <div class="no-tags body1 display-block mb-8">Add a criteria to tune search results.</div>
    <el-row type="flex" :gutter="8">      
      <div class="querybuilder-toolbar_input_datetime toolbar_input ml-4">
        <span class="body2">Search Events During:</span>
        <el-button
          class="text-ellipsis"
          type="primary"
          @click="showTimePicker"
          v-if="!$_.isEmpty(queryCriteria.timeSelection.choice)"
        >
          {{ queryCriteria.timeSelection.choice.label }} <i class="el-icon-arrow-down el-icon--right"></i>
        </el-button>
        <el-button class="text-ellipsis" type="primary" @click="showTimePicker" v-else>
          {{ queryCriteria.timeSelection.startTime }}:{{ queryCriteria.timeSelection.endTime }}
          <i class="el-icon-arrow-down el-icon--right"></i>
        </el-button>
        <el-date-picker
          v-model="userTimeSelection"
          type="datetimerange"
          :popper-class="'custom-time-picker'"
          :picker-options="timePickerShortcutOptions"
          @change="submitDateRangeChange"
          align="left"
          ref="customTimePicker"
          :key="dateChangeIndex"
        >
        </el-date-picker>
      </div>
      <div class="querybuilder-toolbar_input_eventtype toolbar_input" :class="{ 'show-criteria': showCriteria }">
        <el-row v-if="showCriteria">
          <span class="subtitle2 display-inline-block">On:</span>
          <span class="subtitle2 display-inline-block eventtype-button" @click="showCriteria = false">
            <div class="cursor-pointer text-ellipsis display-inline-block">
              <span class="subtitle2 bold">
                {{ getEventTypeOperatorLabel(queryCriteria.eventType.operator) }}
                {{ hasAllEventClass() ? 'All' : getQueryCriteriaEventTypeValues().join(':') }}
              </span>
            </div>
            <i class="display-inline-block material-icons c-icon">created</i>
          </span>
        </el-row>
        <el-row type="flex" v-else>
          <div class="align-top event-operator_input">
            <span class="align-top">
              <span class="subtitle2">On:</span>
              <el-select
                class="subtitle2"
                v-model="queryCriteria.eventType.operator"
                clearable
                placeholder="Event Type *"
                style="width: 80%"
              >
                <span class="subtitle1" style="padding: 0px 20px">* EVENT TYPE IS REQUIRED</span>
                <el-option
                  v-for="item in queryCriteria.eventType.operators"
                  :key="item"
                  :label="getEventTypeOperatorLabel(item)"
                  :value="item"
                >
                  <span>{{ getOperatorLabel(item) }}</span>
                </el-option>
              </el-select>
            </span>
          </div>
          <div class="event-select_input">
            <el-dropdown
              class="full-width"
              v-if="!$_.isEmpty(queryCriteria.eventType.operator)"
              trigger="click"
              :placement="`bottom-end`"
              :hide-on-click="false"
              @command="selectEventTypeValues"
              @visible-change="queryEventType"
              ref="queryBuilderFieldRef"
            >
              <div class="eventtype-tag-section tag-section cursor-pointer">
                <span class="no-tags display-block" v-if="$_.isEmpty(queryCriteria.eventType.values)"
                  >Select Event Type <i class="el-icon-arrow-down el-icon--right"></i
                ></span>
                <el-tag
                  v-else
                  v-for="(tag, idx) in queryCriteria.eventType.values"
                  :key="idx"
                  :disable-transitions="false"
                  @close="selectEventTypeValues(tag)"
                  closable
                  type="info"
                  size="small"
                >
                  <span class="text-ellipsis">{{ tag.label }}</span>
                </el-tag>
              </div>
              <el-dropdown-menu class="criteria-fields_menu" slot="dropdown">
                <div v-for="eventType in threatHuntingEventTypes" :key="eventType.value">
                  <el-dropdown-item
                    class="display-block eventtype-group subtitle2"
                    :class="{ selected: $_.some(queryCriteria.eventType.selected, { value: eventType.value }) }"
                    :label="eventType.label"
                    :value="eventType.value"
                    :command="eventType"
                  >
                    {{ eventType.label }}
                  </el-dropdown-item>
                  <el-dropdown-item
                    class="display-block eventtype-group_options subtitle2"
                    v-for="item in eventType.values"
                    :key="item.value"
                    :class="{ selected: $_.some(queryCriteria.eventType.selected, { value: item.value }) }"
                    :label="item.label"
                    :value="item.value"
                    :command="item"
                  >
                    {{ item.label }}
                  </el-dropdown-item>
                </div>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </el-row>
      </div>
      <div class="querybuilder-toolbar_input_criteria toolbar_input" v-if="showCriteria">
        <el-row type="flex" :gutter="8">
          <div class="criteria_input__field">
            <span class="subtitle2">Add:</span>
            <el-dropdown
              class="criteria-fields"
              trigger="click"
              :placement="`bottom-start`"
              :hide-on-click="false"
              @command="selectCriteriaField"
              ref="queryBuilderFieldRef"
            >
              <span class="el-select cursor-pointer" v-if="$_.isEmpty(queryCriteria.selectedField.name)">
                <i class="el-icon-plus float-left"></i> <span>Criteria</span>
              </span>
              <span class="el-select cursor-pointer" v-else>
                <span>{{ queryCriteria.selectedField.label }}</span> <i class="el-icon-arrow-down el-icon--right"></i>
              </span>
              <el-dropdown-menu class="criteria-fields_menu" slot="dropdown" style="overflow: hidden">
                <section>
                  <el-row type="flex">
                    <el-col :span="10">
                      <span
                        class="display-block subtitle2"
                        v-for="queryField in threatHuntingQueryFields"
                        :key="queryField.name"
                        :class="{ selected: queryField.name === queryCriteria.selectedFieldGroup.name }"
                        @click="queryCriteria.selectedFieldGroup = queryField"
                      >
                        <i class="material-icons">{{ queryField.icon }}</i
                        >{{ queryField.label }}
                      </span>
                    </el-col>
                    <el-col :span="14">
                      <el-dropdown-item
                        class="display-block subtitle2"
                        v-for="field in queryCriteria.selectedFieldGroup.fields"
                        :key="field.name"
                        :label="field.label"
                        :value="field.name"
                        :disabled="field.disabled"
                        :command="field"
                      >
                        {{ field.label }}
                      </el-dropdown-item>
                    </el-col>
                  </el-row>
                </section>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
          <div class="criteria_input__operator">
            <el-select
              v-if="!$_.isEmpty(queryCriteria.selectedField.name)"
              v-model="queryCriteria.filters.operator"
              placeholder="Select"
            >
              <el-option
                v-for="(operator, idx) in queryCriteria.selectedField.operators"
                :key="idx"
                :label="operator"
                :value="operator"
              >
                <span>{{ getOperatorLabel(operator) }}</span>
              </el-option>
            </el-select>
          </div>
          <div class="criteria_input__select">
            <span v-if="!$_.isEmpty(queryCriteria.filters.operator)">
              <el-select
                class="full-width"
                v-if="queryCriteria.selectedField.datatype === 'select'"
                v-model="queryCriteria.filters.values"
                filterable
                placeholder="Select values"
                :multiple="$_.indexOf(['in', 'not_in'], queryCriteria.filters.operator) > -1"
              >
                <el-option
                  v-for="item in queryCriteriaValues"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                >
                </el-option>
              </el-select>
              <el-input
                v-else
                type="textarea"
                :autosize="{ minRows: 1, maxRows: 4 }"
                :placeholder="
                  `${
                    $_.indexOf(['in', 'not_in'], queryCriteria.eventType.operator) < 0
                      ? 'Provide input'
                      : 'Provide comma separated inputs'
                  }`
                "
                v-model="queryCriteria.filters.values"
              >
              </el-input>
            </span>
          </div>
        </el-row>
      </div>
      <div class="box icons-tools float-right">
        <div class="criteria_input__add display-inline-block">
          <span class="criteria-add" @click="submitQuery"> GO </span>
        </div>
        <el-divider direction="vertical"></el-divider>
        <el-button-group>
          <el-tooltip class="item" effect="dark" content="Reset Criteria" placement="top" :enterable="false">
            <el-button type="text" class="cursor-pointer" @click="resetAllFields(true, true)">
              <i class="material-icons c-icon">refresh</i>
            </el-button>
          </el-tooltip>
          <el-tooltip class="item" effect="dark" content="Save" placement="top" :enterable="false">
            <el-popover
              placement="bottom-start"
              width="260"
              v-model="saveVisible"
              popper-class="savedsearch-popover"
              :disabled="!showCriteria"
              style="background: white;"
            >
              <el-form :model="savedSearchForm" :rules="savedSearchNameValidator" ref="saveSearchForm">
                <p>Save the search query.</p>
                <el-form-item class="display-block" prop="name">
                  <el-input v-model="savedSearchForm.name" placeholder="Title" autocomplete="off"></el-input>
                </el-form-item>
              </el-form>
              <div style="text-align: right; margin: 0">
                <el-button size="mini" @click="saveVisible = false">cancel</el-button>
                <el-button size="mini" type="primary" @click="saveSearchQuery">Save</el-button>
              </div>
              <el-button slot="reference" type="text" class="cursor-pointer">
                <i class="material-icons c-icon">save</i>
              </el-button>
            </el-popover>
          </el-tooltip>
        </el-button-group>
      </div>
    </el-row>
    <el-row type="flex">      
      <div class="tag-section" v-if="!$_.isEmpty(criteriaTags)">        
        <el-tag          
          v-for="(tag, idx) in criteriaTags"
          closable
          :key="tag"
          :disable-transitions="false"
          @close="removeFilter(tag, idx)"
        >
          <span class="text-ellipsis">{{ tag }}</span>
        </el-tag>
      </div>
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import VueTagsInput from '@johmun/vue-tags-input'
import { queryNameRe } from '@/utilities/regex/basic'
import { getInsightsDateTimeRange, getInsightsDateRangeChoice } from '@/utilities/datetime.helper'
import { INSIGHTS_DATE_RANGE_CHOICES } from '@/constants/datetime.constants'
import DateRangeDropdown from '@/components/dropdown/DateRangeDropdown.vue'

import { TRAILSINFO_LIST_REQUEST, TRAILSINFO_LIST_SUCCESS } from '@/store/modules/trails/trailsinfo.type'
import { AGENT_LIST_REQ } from '@/store/modules/agents/agents.type'
import {
  THREAT_HUNTING_FIELDS_REQ,
  THREAT_HUNTING_FIELDS_SUCCESS,
  THREAT_HUNTING_EVENT_TYPES_REQ,
  THREAT_HUNTING_EVENT_TYPES_SUCCESS,
  THREAT_HUNTING_RESET_STATUS,
  THREAT_HUNTING_FIELDS_RESET_STATUS,
  THREAT_HUNTING_EVENT_TYPES_RESET_STATUS
} from '@/store/modules/threat_hunting/query-fields.type'

import {
  ALL_SAVED_SEARCH_REQ,
  ALL_SAVED_SEARCH_SUCCESS,
  SAVED_SEARCH_POST,
  SAVED_SEARCH_POST_SUCCESS
} from '@/store/modules/threat_hunting/saved-search.type'

import { THREAT_HUNTING_DATE_RANGE_PREFERENCE } from '@/store/modules/user_pref/user-pref.type'

export default {
  name: 'QueryBuilder',
  props: {
    queryFilters: {
      type: [Object, Array],
      default: () => {
        return {}
      }
    }
  },
  data () {
    return {
      dateChangeIndex: 0,
      daterangeUserPrefConstant: THREAT_HUNTING_DATE_RANGE_PREFERENCE,
      localLocationQueryUpdate: false,
      saveVisible: false,
      showCriteria: false,
      selectEventTypeVisiblity: false,
      savedSearchForm: {
        name: ''
      },
      savedSearchNameValidator: {
        name: [
          {
            required: true,
            trigger: 'blur',
            validator: function (rule, value, callback) {
              if (!value || !value.length) {
                callback(new Error('Please provide the search name'))
              } else if (value.length > 256) {
                callback(new Error('Search name length exceeds 256'))
              } else {
                queryNameRe.test(value) ? callback() : callback(new Error('Invalid search name format'))
              }
            }
          }
        ]
      },
      criteriaTags: [],
      queryCriteria: {
        timeSelection: {},
        eventType: {
          name: 'edgeName',
          datatype: 'string',
          operators: ['in', 'not_in', 'equal', 'not_equal'],
          operator: '',
          values: [],
          selected: [] // for dropdown display purposes
        },
        filters: {
          field: {},
          operator: '',
          values: ''
        },
        selectedFieldGroup: {
          fields: []
        },
        selectedField: {
          field: {},
          operator: '',
          values: ''
        }
      },
      criteriaFilters: [],
      userTimeSelection: '',
      timePickerShortcutOptions: {
        disabledDate (time) {
          return time.getTime() > Date.now()
        },
        shortcuts: [
          {
            text: INSIGHTS_DATE_RANGE_CHOICES[0].label,
            onClick (picker) {
              picker.$emit('pick', {
                date: getInsightsDateTimeRange(INSIGHTS_DATE_RANGE_CHOICES[0].value),
                choice: INSIGHTS_DATE_RANGE_CHOICES[0]
              })
            }
          },
          {
            text: INSIGHTS_DATE_RANGE_CHOICES[1].label,
            onClick (picker) {
              picker.$emit('pick', {
                date: getInsightsDateTimeRange(INSIGHTS_DATE_RANGE_CHOICES[1].value),
                choice: INSIGHTS_DATE_RANGE_CHOICES[1]
              })
            }
          },
          {
            text: INSIGHTS_DATE_RANGE_CHOICES[2].label,
            onClick (picker) {
              picker.$emit('pick', {
                date: getInsightsDateTimeRange(INSIGHTS_DATE_RANGE_CHOICES[2].value),
                choice: INSIGHTS_DATE_RANGE_CHOICES[2]
              })
            }
          },
          {
            text: INSIGHTS_DATE_RANGE_CHOICES[3].label,
            onClick (picker) {
              picker.$emit('pick', {
                date: getInsightsDateTimeRange(INSIGHTS_DATE_RANGE_CHOICES[3].value),
                choice: INSIGHTS_DATE_RANGE_CHOICES[3]
              })
            }
          },
          {
            text: INSIGHTS_DATE_RANGE_CHOICES[4].label,
            onClick (picker) {
              picker.$emit('pick', {
                date: getInsightsDateTimeRange(INSIGHTS_DATE_RANGE_CHOICES[4].value),
                choice: INSIGHTS_DATE_RANGE_CHOICES[4]
              })
            }
          },
          {
            text: INSIGHTS_DATE_RANGE_CHOICES[5].label,
            onClick (picker) {
              picker.$emit('pick', {
                date: getInsightsDateTimeRange(INSIGHTS_DATE_RANGE_CHOICES[5].value),
                choice: INSIGHTS_DATE_RANGE_CHOICES[5]
              })
            }
          },
          {
            text: INSIGHTS_DATE_RANGE_CHOICES[6].label,
            onClick (picker) {
              picker.$emit('pick', {
                date: getInsightsDateTimeRange(INSIGHTS_DATE_RANGE_CHOICES[6].value),
                choice: INSIGHTS_DATE_RANGE_CHOICES[6]
              })
            }
          },
          {
            text: INSIGHTS_DATE_RANGE_CHOICES[7].label,
            onClick (picker) {
              picker.$emit('pick', {
                date: getInsightsDateTimeRange(INSIGHTS_DATE_RANGE_CHOICES[7].value),
                choice: INSIGHTS_DATE_RANGE_CHOICES[7]
              })
            }
          }
        ]
      }
    }
  },
  methods: {
    getOperatorLabel (operator) {
      switch (operator) {
        case 'in':
          return `In`
        case 'not_in':
          return `Not In`
        case 'not_equal':
          return `Not Equal`
        case 'matches':
          return `Matches`
        case 'equal':
          return `Equal`
        default:
          return `Equal`
      }
    },
    getEventTypeOperatorLabel (operator) {
      let label = 'Event Type'
      switch (operator) {
        case 'in':
          return `${label}: in: `
        case 'not_in':
          return `${label}: not_in: `
        case 'not_equal':
          return `${label}: not_eq: `
        case 'equal':
          return `${label}: eq: `
        default:
          return `Event Type *`
      }
    },
    showTimePicker () {
      this.$refs.customTimePicker.showPicker()
    },
    getDateRangeUrlString () {
      if (_.has(this.queryCriteria.timeSelection, 'startTime') || _.has(this.queryCriteria.timeSelection, 'endTime')) {
        return `start_time:${this.queryCriteria.timeSelection.startTime}+end_time:${this.queryCriteria.timeSelection.endTime}`
      }
      return `date_range:${getInsightsDateTimeRange(this.queryCriteria.timeSelection.value).value}`
    },
    getQueryCriteriaEventTypeValues () {
      return _.map(this.queryCriteria.eventType.values, 'value')
    },
    hasAllEventClass () {
      let eventClasses = this.getQueryCriteriaEventTypeValues()
      let containsAll = ['exec', 'file', 'network', 'system', 'user'].every(i => eventClasses.includes(i))
      return containsAll
    },
    getQueryCriteriaEventType () {
      return {
        name: this.queryCriteria.eventType.name,
        key: this.queryCriteria.eventType.key,
        datatype: this.queryCriteria.eventType.datatype,
        operator: this.queryCriteria.eventType.operator,
        values: this.getQueryCriteriaEventTypeValues()
      }
    },
    enableAllCriteriaFields () {
      for (let queryFieldGroup of this.threatHuntingQueryFields) {
        for (let field of queryFieldGroup.fields) {
          field.disabled = false
        }
      }
    },
    resetAllFields (resetTable = false, resetRoute = false) {
      this.userTimeSelection = getInsightsDateTimeRange(INSIGHTS_DATE_RANGE_CHOICES[3].value)
      this.criteriaTags.length = 0
      this.criteriaFilters.length = 0
      _.assign(this.queryCriteria, {
        timeSelection: {
          startTime: this.userTimeSelection[0].toISOString(),
          endTime: this.userTimeSelection[1].toISOString(),
          choice: INSIGHTS_DATE_RANGE_CHOICES[3]
        },
        eventType: {
          name: 'edgeName',
          datatype: 'string',
          operators: ['in', 'not_in', 'equal', 'not_equal'],
          operator: '',
          values: [],
          selected: [] // for dropdown display purposes
        },
        filters: {
          field: {},
          operator: '',
          values: ''
        },
        selectedFieldGroup: {
          fields: []
        },
        selectedField: {
          field: {},
          operator: '',
          values: ''
        }
      })
      this.resetQueryCriteriaField()
      if (resetRoute) {
        this.$router.replace({
          query: {
            search: ''
          }
        })
      }
      if (resetTable) {
        this.$emit('reset-query-results')
      }
      if (this.threatHuntingEventTypesReqStatus === 'saved') {
        this.enableAllCriteriaFields()
      }
    },
    /**
     * threat-hunting?search=date_range:PAST_1_MONTH
     * +edgeName:in:ThreadStart:ImageLoad:file
     * +os:in:Windows:Linux+agentId:in:prod_0_9_.agent-9:prod_0_3_.agent-3:prod_0_23_.agent-23:prod_0_1_.agent-1
     */
    setQueryCriteriaFieldFromParam (routeQueryParam) {
      let queryParams = routeQueryParam.split('+')

      for (let idx = 0; idx < queryParams.length; idx++) {
        let queryParam = queryParams[idx].split(':')
        if (queryParam[0] === 'start_time') {
          this.queryCriteria.timeSelection.startTime = queryParam.slice(1, queryParam.length).join(':')
          this.queryCriteria.timeSelection.choice = undefined
        } else if (queryParam[0] === 'end_time') {
          this.queryCriteria.timeSelection.endTime = queryParam.slice(1, queryParam.length).join(':')
          this.queryCriteria.timeSelection.choice = undefined
        } else if (queryParam[0] === 'date_range') {
          let dateRange = getInsightsDateTimeRange(queryParam[1])
          this.userTimeSelection = dateRange
          this.queryCriteria.timeSelection = {
            startTime: dateRange[0].toISOString(),
            endTime: dateRange[1].toISOString(),
            choice: getInsightsDateRangeChoice(queryParam[1])
          }
        } else if (queryParam[0] === 'edgeName') {
          this.queryCriteria.eventType.operator = queryParam[1]
          for (let idx = 2; idx < queryParam.length; idx++) {
            for (let eventClass of this.threatHuntingEventTypes) {
              if (queryParam[idx] === eventClass.value) {
                this.queryCriteria.eventType.values.push(eventClass)
                this.queryCriteria.eventType.selected.push(eventClass)
                continue
              }
              for (let eventType of eventClass.values) {
                if (queryParam[idx] === eventType.value) {
                  this.queryCriteria.eventType.values.push(eventType)
                  this.queryCriteria.eventType.selected.push(eventType)
                  continue
                }
              }
            }
          }
        } else {
          // first slot is name
          // second slot is operator
          let addCriteria = {
            name: queryParam[0],
            operator: queryParam[1],
            values: []
          }

          for (let queryFieldGroup of this.threatHuntingQueryFields) {
            for (let field of queryFieldGroup.fields) {
              if (field.name === addCriteria.name) {
                field.disabled = true
                addCriteria.label = field.label
                addCriteria.datatype = field.datatype
                addCriteria.operators = field.operators
                break
              }
            }
          }

          // >=3  slot is values
          if (addCriteria.name === 'trailId' && queryParam.length > 3) {
            let criteriaTrailId = ''
            for (let idx = 2; idx < queryParam.length; idx++) {
              if (idx % 2 === 0) {
                criteriaTrailId = queryParam[idx]
              } else {
                criteriaTrailId = `${criteriaTrailId}:${queryParam[idx]}`
                if (_.indexOf(['in', 'not_in'], addCriteria.operator) > -1) {
                  addCriteria.values.push(criteriaTrailId)
                } else {
                  addCriteria.values = criteriaTrailId
                }
              }
            }
          } else {
            for (let idx = 2; idx < queryParam.length; idx++) {
              if (_.indexOf(['in', 'not_in'], addCriteria.operator) > -1) {
                addCriteria.values.push(queryParam[idx])
              } else {
                addCriteria.values = queryParam[idx]
              }
            }
          }

          if (_.isArray(addCriteria.values)) {
            if (addCriteria.name === 'agentId') {
              this.criteriaTags.push(
                `${addCriteria.label}: ${addCriteria.operator}: ${this.replaceAgentIdWithHostnames(addCriteria.values)}`
              )
            } else {
              this.criteriaTags.push(`${addCriteria.label}: ${addCriteria.operator}: ${addCriteria.values.join(':')}`)
            }
          } else {
            if (addCriteria.name === 'agentId') {
              this.criteriaTags.push(
                `${addCriteria.label}: ${addCriteria.operator}: ${this.replaceAgentIdWithHostnames(addCriteria.values)}`
              )
            } else {
              this.criteriaTags.push(`${addCriteria.label}: ${addCriteria.operator}: ${addCriteria.values}`)
            }
          }
          this.criteriaFilters.push(addCriteria)
        }
      }

      if (_.isEmpty(this.queryCriteria.timeSelection.choice)) {
        this.userTimeSelection = [this.queryCriteria.timeSelection.startTime, this.queryCriteria.timeSelection.endTime]
      }

      this.showCriteria = true
      this.$emit('submit-query', {
        dateRange: this.queryCriteria.timeSelection,
        eventType: this.getQueryCriteriaEventType(),
        filters: this.criteriaFilters
      })
      this.resetQueryCriteriaField()
    },
    setRouterQueryParam (eventType, queryFilters) {
      let queryFiltersString = ''
      for (let filter of queryFilters) {
        if (_.isArray(filter.values)) {
          queryFiltersString += `+${filter.name}:${filter.operator}:${filter.values.join(':')}`
        } else {
          queryFiltersString += `+${filter.name}:${filter.operator}:${filter.values}`
        }
      }
      this.localLocationQueryUpdate = true
      this.$router.replace({
        query: {
          search: `${this.getDateRangeUrlString()}+${eventType.name}:${eventType.operator}:${eventType.values.join(
            ':'
          )}${queryFiltersString}`
        }
      })
      this.$nextTick(() => {
        this.localLocationQueryUpdate = false
      })
    },
    submitDateRangeChange (values) {
      // user selected custom time range
      this.queryCriteria.timeSelection.choice = values.choice
      if (!_.isEmpty(values.choice)) {
        values = values.date
      }

      this.userTimeSelection = values
      this.queryCriteria.timeSelection.startTime = values[0].toISOString()
      this.queryCriteria.timeSelection.endTime = values[1].toISOString()

      if (!_.isEmpty(this.queryCriteria.eventType.values)) {
        this.setRouterQueryParam(this.getQueryCriteriaEventType(), this.criteriaFilters)
        this.$emit('submit-query', {
          dateRange: this.queryCriteria.timeSelection,
          eventType: this.getQueryCriteriaEventType(),
          filters: this.criteriaFilters
        })
        this.showCriteria = true
      }
    },
    /**
     * Event Type Value Selection
     * 1. If parent type class is selected, all children types are selected
     * 2. If child type is selected, other selections unaffected
     * 3. if parent type class is unselected, all children types are unselected
     * 4. If child type is unselected, check if parent is selected and unselect parent and select all other child types
     */
    selectEventTypeValues (value) {
      // unselect
      if (_.some(this.queryCriteria.eventType.selected, { value: value.value })) {
        // remove from values list (part of POST body)
        this.removeEventTypeValues(value, _.findIndex(this.queryCriteria.eventType.values, { value: value.value }))
        // unselect from selected property - dropdown item display
        let foundIdx = _.findIndex(this.queryCriteria.eventType.selected, { value: value.value })
        if (foundIdx > -1) {
          this.queryCriteria.eventType.selected.splice(foundIdx, 1)
        }
        // if unselect is a class, unselect all children types - dropdown item display
        if (value.type === 'class') {
          for (let childType of value.values) {
            let foundIdx = _.findIndex(this.queryCriteria.eventType.selected, { value: childType.value })
            if (foundIdx > -1) {
              this.queryCriteria.eventType.selected.splice(foundIdx, 1)
            }
            // remove from values
            let foundValIdx = _.findIndex(this.queryCriteria.eventType.values, { value: childType.value })
            if (foundValIdx > -1) {
              this.queryCriteria.eventType.values.splice(foundValIdx, 1)
            }
          }
        } else {
          // If child type is unselected, check if parent is selected and unselect parent
          // find parent class of unselected child type
          let parentIdx = _.findIndex(this.queryCriteria.eventType.selected, { value: value.parentClass })
          // if parent class is selected, remove parent class selection while keeping all other child event types selected
          if (parentIdx > -1) {
            // remove parent class selection for values list (POST body)
            let parentValueIdx = _.findIndex(this.queryCriteria.eventType.values, { value: value.parentClass })
            this.removeEventTypeValues({}, parentValueIdx)
            // select all other child event types
            for (let childType of this.queryCriteria.eventType.selected[parentIdx].values) {
              if (childType.value !== value.value) {
                // only add to tag values list if it's not already there
                if (!_.some(this.queryCriteria.eventType.values, { value: childType.value })) {
                  this.queryCriteria.eventType.values.push(childType)
                }
              }
            }
            // remove parent class selection
            this.queryCriteria.eventType.selected.splice(parentIdx, 1)
          }
        }
        return true
      }
      this.queryCriteria.eventType.values.push(value)
      this.queryCriteria.eventType.selected.push(value)

      // select class type and all child types
      if (value.type === 'class') {
        for (let childType of value.values) {
          // check child type in the dropdown
          if (!_.some(this.queryCriteria.eventType.selected, { value: childType.value })) {
            this.queryCriteria.eventType.selected.push(childType)
          }
          // remove child type in the tag list
          let foundValIdx = _.findIndex(this.queryCriteria.eventType.values, { value: childType.value })
          if (foundValIdx > -1) {
            this.queryCriteria.eventType.values.splice(foundValIdx, 1)
          }
        }
      }
    },
    removeEventTypeValues (value, idx) {
      if (idx > -1) {
        this.queryCriteria.eventType.values.splice(idx, 1)
      }
    },
    updateEventTypeSelection (selValue) {
      let lastSelectedValue = selValue[selValue.length - 1]
      let eventClass = _.find(this.threatHuntingEventTypes, { class: lastSelectedValue })
      if (eventClass) {
        for (let eventType of eventClass.values) {
          if (!_.some(selValue, eventType.value)) {
            // this.queryCriteria.eventType.values.push(eventType.value);
          }
        }
      }
    },
    queryEventType (visible) {
      this.selectEventTypeVisiblity = visible
    },
    selectCriteriaField (field) {
      this.queryCriteria.selectedField = field
      this.queryCriteria.filters.field = field
      this.$refs.queryBuilderFieldRef.hide()
    },
    resetQueryCriteriaField () {
      this.queryCriteria.selectedField = {
        field: {},
        operator: '',
        values: ''
      }
      this.queryCriteria.filters = {
        field: {},
        operator: '',
        values: ''
      }
    },
    removeFilter (tag, filterIndex) {
      for (let queryField of this.threatHuntingQueryFields) {
        let removedCriteria = _.find(queryField.fields, { name: this.criteriaFilters[filterIndex].name })
        if (removedCriteria) {
          removedCriteria.disabled = false
          break
        }
      }
      this.criteriaTags.splice(filterIndex, 1)
      this.criteriaFilters.splice(filterIndex, 1)
      this.setRouterQueryParam(this.getQueryCriteriaEventType(), this.criteriaFilters)
      this.$emit('submit-query', {
        dateRange: this.queryCriteria.timeSelection,
        eventType: this.getQueryCriteriaEventType(),
        filters: this.criteriaFilters
      })
    },
    submitQuery () {
      this.showCriteria = true
      this.queryCriteria.selectedField.disabled = true

      // Submit query on event type selection
      if (!_.isEmpty(this.queryCriteria.eventType.values) && _.isEmpty(this.queryCriteria.filters.values)) {
        let queryEventType = this.getQueryCriteriaEventType()
        this.setRouterQueryParam(queryEventType, this.criteriaFilters)
        this.$emit('submit-query', {
          dateRange: this.queryCriteria.timeSelection,
          eventType: queryEventType,
          filters: this.criteriaFilters
        })

        return
      }

      // Submit query on criteria addition
      if (!_.isEmpty(this.queryCriteria.filters.values)) {
        let addCriteria = {
          datatype: this.queryCriteria.filters.field.datatype,
          name: this.queryCriteria.filters.field.name,
          label: this.queryCriteria.filters.field.label,
          operator: this.queryCriteria.filters.operator,
          values: this.queryCriteria.filters.values
        }

        // convert comma separated string to array list
        if (_.indexOf(['in', 'not_in'], addCriteria.operator) > -1 && !_.isArray(addCriteria.values)) {
          let criteriaStrValues = addCriteria.values.split(/(?:,| )+/) // ignores empty between commas
          addCriteria.values = []
          criteriaStrValues.forEach(element => {
            if (!_.isEmpty(element)) {
              addCriteria.values.push(element)
            }
          })
        }

        this.criteriaFilters.push(addCriteria)
        if (_.isArray(addCriteria.values)) {
          if (addCriteria.name === 'agentId') {
            this.criteriaTags.push(
              `${addCriteria.label}: ${addCriteria.operator}: ${this.replaceAgentIdWithHostnames(addCriteria.values)}`
            )
          } else {
            this.criteriaTags.push(`${addCriteria.label}: ${addCriteria.operator}: ${addCriteria.values.join(':')}`)
          }
        } else {
          if (addCriteria.name === 'agentId') {
            this.criteriaTags.push(
              `${addCriteria.label}: ${addCriteria.operator}: ${this.replaceAgentIdWithHostnames(addCriteria.values)}`
            )
          } else {
            this.criteriaTags.push(`${addCriteria.label}: ${addCriteria.operator}: ${addCriteria.values}`)
          }
        }
        this.setRouterQueryParam(this.getQueryCriteriaEventType(), this.criteriaFilters)
        this.$emit('submit-query', {
          dateRange: this.queryCriteria.timeSelection,
          eventType: this.getQueryCriteriaEventType(),
          filters: this.criteriaFilters
        })
        this.resetQueryCriteriaField()
      }
    },
    saveSearchQuery () {
      this.$refs['saveSearchForm'].validate(valid => {
        if (valid) {
          if (_.some(this.allSavedSearches.models, { name: this.savedSearchForm.name })) {
            let valueFieldValidator = _.find(this.$refs['saveSearchForm'].fields, {
              prop: 'name'
            })
            valueFieldValidator.$el.classList.add('is-error')
            valueFieldValidator.validateState = 'error'
            valueFieldValidator.validateMessage = 'Query name already exists.'
            return false
          }
          this.$store
            .dispatch(SAVED_SEARCH_POST, {
              savedSearchName: this.savedSearchForm.name,
              searchQuery: this.$route.query.search
            })
            .then(
              () => {
                this.$store.dispatch(ALL_SAVED_SEARCH_REQ).then(() => {
                  this.$store.dispatch(ALL_SAVED_SEARCH_SUCCESS)
                })
                this.$store.dispatch(SAVED_SEARCH_POST_SUCCESS)
                this.saveVisible = false
                this.$message({
                  type: 'success',
                  message: `Saved query ${this.savedSearchForm.name}`
                })
              },
              err => {
                console.log(err)
                this.$message({
                  type: 'error',
                  message: `Error saving query ${this.savedSearchForm.name}`
                })
              }
            )
        }
      })
    },
    getQueryFieldsAndTypes () {
      return new Promise((resolve, reject) => {
        this.$store.dispatch(THREAT_HUNTING_EVENT_TYPES_REQ).then(
          () => {
            this.$store.dispatch(THREAT_HUNTING_EVENT_TYPES_SUCCESS)
            this.$store.dispatch(THREAT_HUNTING_FIELDS_REQ).then(() => {
              this.queryCriteria.selectedFieldGroup = this.threatHuntingQueryFields[0]
              this.$store.dispatch(THREAT_HUNTING_FIELDS_SUCCESS)
              resolve()
            })
          },
          err => {
            reject(err)
          }
        )
      })
    },
    replaceAgentIdWithHostnames (agentIds) {
      if (_.isArray(agentIds)) {
        let hostnames = ''
        agentIds.forEach((agentId, indx) => {
          let agent = _.find(this.agentList.models, { agentId: agentId })
          if (agent) {
            hostnames += `${agent.customHostname}`
          } else {
            hostnames += `${agentId}`
          }
          if (indx !== agentIds.length - 1) {
            hostnames += ':'
          }
        })
        return hostnames
      }
      let agent = _.find(this.agentList.models, { agentId: agentIds })
      if (agent) {
        return agent.customHostname
      }
      return agentIds
    }
  },
  created () {
    this.getQueryFieldsAndTypes()
    this.$nextTick(() => {
      if (this.agentListReqStatus !== 'saved') {
        this.$store.dispatch(AGENT_LIST_REQ).then(resp => {
          let hostFitlerIdx = _.findIndex(this.threatHuntingQueryFields, { name: 'host' })
          if (hostFitlerIdx > -1) {
            let agentIdFitlerIdx = _.findIndex(this.threatHuntingQueryFields[hostFitlerIdx].fields, {
              name: 'agentId'
            })
            this.threatHuntingQueryFields[hostFitlerIdx].fields[agentIdFitlerIdx].datatype = 'select'
            this.threatHuntingQueryFields[hostFitlerIdx].fields[agentIdFitlerIdx].values = _.map(
              this.agentList.models,
              agent => {
                return { label: agent.customHostname, value: agent.agentId }
              }
            )
          }
        })
      }
      if (this.trailsinfoReqStatus !== 'saved') {
        this.$store.dispatch(TRAILSINFO_LIST_REQUEST).then(trails => {
          this.$store.dispatch(TRAILSINFO_LIST_SUCCESS)
        })
      }
      if (_.isEmpty(this.$route.query.search)) {
        this.resetAllFields()
      }
    })
  },
  watch: {
    '$route.query.search': {
      handler: function (newVal, oldVal) {
        if (!this.localLocationQueryUpdate) {
          if (!_.isEmpty(newVal) && newVal !== oldVal) {
            // clear all selection
            this.resetAllFields()
            if (
              this.threatHuntingQueryFieldsReqStatus !== 'saved' ||
              this.threatHuntingEventTypesReqStatus !== 'saved'
            ) {
              this.getQueryFieldsAndTypes().then(() => {
                this.setQueryCriteriaFieldFromParam(this.$route.query.search)
              })
            } else {
              this.setQueryCriteriaFieldFromParam(this.$route.query.search)
            }
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    ...mapGetters([
      'threatHuntingDateRangeUserSetting',
      'agentListReqStatus',
      'agentList',
      'trailsinfoReqStatus',
      'threatHuntingQueryFieldsReqStatus',
      'threatHuntingQueryFields',
      'threatHuntingEventTypesReqStatus',
      'threatHuntingEventTypes',
      'allSavedSearches'
    ]),
    queryCriteriaValues () {
      return this.queryCriteria.selectedField.values.filter(item => {
        return item.label.toLowerCase() !== 'unnamed';
      })
    }
  },
  beforeDestroy () {
    // use empty function to kill interval update calls (timing insurance)
    this.$store.dispatch(THREAT_HUNTING_RESET_STATUS)
    this.$store.dispatch(THREAT_HUNTING_FIELDS_RESET_STATUS)
    this.$store.dispatch(THREAT_HUNTING_EVENT_TYPES_RESET_STATUS)
  },
  components: { VueTagsInput, DateRangeDropdown }
}
</script>

<style>
>>> .el-popover.el-popper.savedsearch-popover {
  background: white;
}

>>> .el-date-range-picker.custom-time-picker {
  top: 125px !important;
}
</style>

<style scoped>
@import 'assets/styles/_variables.css';
@import 'assets/styles/colors.css';
.toolbar_input {
  margin-right: 5px;
}

.querybuilder-toolbar_input_datetime {
  height: 32px;
  width: 260px;

  .el-button {
    max-width: calc(100%-135px);
    line-height: 32px;
    padding: 0px 5px;
    border: 1px solid var(--border-color);
    border-radius: 2px;
    color: white;
    background-color: var(--denim-600);
    font-weight: bold;
    font-size: 12px;
  }

  >>> .el-date-editor {
    height: 0px;
    width: 0px;
    left: -75px;
    visibility: hidden;
    vertical-align: middle;
  }

  .daterange-dropdown {
    >>> .el-dropdown {
      border: 1px solid var(--border-color);
      border-radius: 2px;
      color: white;
      background-color: var(--denim-600);
      font-weight: bold;
      font-size: 12px;
    }

    >>> .icon {
      display: none;
    }
  }
}

.querybuilder-toolbar_input_eventtype {
  width: calc(100% - 400px);

  .event-operator_input {
    width: 200px;
  }

  .event-select_input {
    width: calc(100% - 200px);
  }

  &.show-criteria {
    width: 225px;
  }
}

.querybuilder-toolbar_input_criteria {
  width: calc(100% - 650px);

  .el-row {
    > div {
      margin-right: 5px;
    }
  }

  .criteria_input__field {
    width: 170px;
  }

  .criteria_input__operator {
    width: 120px;
  }

  .criteria_input__select {
    width: calc(100% - 325px);
  }
}

.icons-tools {
  .criteria_input__add {
    width: 30px;
  }

  .el-divider--vertical {
    height: 1.5em;
  }

  >>> .el-button {
    padding: 0px;

    .material-icons {
      line-height: 32px;
      margin-right: 4px;
    }
  }
}

>>> .el-collapse-item__content {
  padding-bottom: 12px;
}

.el-row {
  margin-bottom: 20px;
  &:last-child {
    margin-bottom: 0;
  }
}

.el-divider--horizontal {
  margin: 12px 0px;
}

.align-top {
  vertical-align: top;
}

>>> .el-input__inner {
  font-size: 14px;
  padding: 6px;
  height: 34px;
  line-height: 34px;
  border-radius: 0px;
  border: 1px solid var(--border-color);
}

>>> .el-textarea__inner {
  font-family: var(--main-font-family);
  min-height: 40px !important;
  height: 40px !important;
}

.material-icons {
  vertical-align: middle;
  color: var(--gray-500);
  line-height: 40px;
}

>>> .vue-tags-input {
  width: 100%;
  max-width: unset;

  input {
    text-align: center;
  }
}

.daterange-dropdown {
  display: inline-block;
}

.criteria-fields {
  &.el-dropdown {
    border: 1px solid var(--border-color);
    border-radius: 2px;
    color: white;
    background-color: var(--denim-600);
    font-weight: bold;
    font-size: 12px;
  }

  .el-select {
    padding: 6px;

    .el-icon-plus {
      font-size: 10px;
      vertical-align: middle;
      line-height: 20px;
    }
  }
}

.criteria-fields_menu {
  width: 350px;
  max-height: 350px;
  box-shadow: 2px 2px var(--aluminum-200);
  padding: 0px;

  section {
    min-width: 280px;
  }

  .el-col:first-of-type {
    border-right: 1px solid var(--border-color);

    i {
      padding-right: 10px;
      line-height: 31px;
      vertical-align: middle;
      font-size: 1.8em;
    }

    span {
      cursor: pointer;
      padding: 2px 18px;

      &:not(:last-child) {
        border-bottom: 1px solid var(--border-color);
      }

      &.selected {
        background-color: #ecf5ff;
      }
    }
  }
}

.eventtype-group {
  padding-left: 10px;
}

.eventtype-group_options {
  padding-left: 25px;
}

.eventtype-button {
  width: 82%;
  padding: 4px 8px;
  border-radius: 2px;
  color: white;
  background-color: var(--denim-600);

  .text-ellipsis {
    width: 90%;
    vertical-align: middle;
  }

  .material-icons {
    width: 5px;
    line-height: 24px;
    color: white;
    margin: 0px;
    vertical-align: top;
  }
}

span.criteria-add {
  cursor: pointer;
  color: white;
  background-color: var(--denim-600);
  padding: 7px;
  border-radius: 2px;

  i {
    font-size: 1.2em;
    padding: 8px 0px;
  }
}

.event-select_input:focus {
  outline: none;
}

.eventtype-tag-section.tag-section {
  height: auto;
  background-color: white;

  .no-tags {
    width: calc(100%-30px);
    background-color: white;
    line-height: 32px;
    text-align: left;
    padding-left: 10px;

    .el-icon-arrow-down {
      float: right;
      padding-right: 20px;
      margin-top: 10px;
    }
  }

  .el-tag {
    color: #909399;
  }
}

.tag-section {
  height: 40px;
  width: 100%;
  border: 1px solid var(--border-color);
  background-color: var(--aluminum-200);

  .no-tags {
    width: 100%;
    vertical-align: middle;
    line-height: 40px;
    text-align: center;
  }

  .el-tag {
    max-width: 250px;
    margin: 4px;
    color: black;
    border: 1px solid var(--border-color);
  }

  >>> .el-tag__close.el-icon-close {
    font-size: 18px;
    color: var(--border-color);
  }

  .text-ellipsis {
    max-width: 215px;
    display: inline-block;
    vertical-align: middle;
  }
}

>>> .el-select .el-input .el-select__caret {
  color: var(--border-color);
}

.el-dropdown-menu__item.selected {
  color: #409eff;
  font-weight: 700;

  &::after {
    position: absolute;
    right: 20px;
    font-family: element-icons;
    content: '\E6DA';
    font-size: 12px;
    font-weight: 700;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

.icon-copy-to-clipboard {
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/query/QueryBuilder.vue